import { useEffect } from "react";

interface MetaTagsOptions {
  title: string;
  description: string;
  ogImage?: string;
  ogType?: string;
  canonicalPath?: string;
  twitterCardType?: string;
  robots?: string;
  geoRegion?: string;
  geoPlacename?: string;
  geoPosition?: string;
  keywords?: string;
}

const BASE_URL = "https://www.flowerpotcottage.com.au";
const DEFAULT_OG_IMAGE = "https://flowerpotcottage.lovable.app/lovable-uploads/83d4a523-b23c-4330-99ff-22861523cb04.png";

function setMetaTag(property: string, content: string, isOg = false) {
  const attr = isOg ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMetaTag(property: string, isOg = false) {
  const attr = isOg ? "property" : "name";
  const el = document.querySelector(`meta[${attr}="${property}"]`);
  if (el) el.remove();
}

function setCanonical(url: string) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

export function useMetaTags({
  title,
  description,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  canonicalPath = "/",
  twitterCardType = "summary_large_image",
  robots,
  geoRegion = "AU-TAS",
  geoPlacename = "Flowerpot",
  geoPosition = "-43.2310;147.0223",
  keywords,
}: MetaTagsOptions) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = title;

    const canonicalUrl = `${BASE_URL}${canonicalPath}`;

    setMetaTag("description", description);
    setMetaTag("og:title", title, true);
    setMetaTag("og:description", description, true);
    setMetaTag("og:image", ogImage, true);
    setMetaTag("og:type", ogType, true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("twitter:card", twitterCardType);
    setMetaTag("twitter:title", title, true);
    setMetaTag("twitter:description", description, true);
    setMetaTag("twitter:image", ogImage, true);
    setCanonical(canonicalUrl);

    // Robots
    if (robots) {
      setMetaTag("robots", robots);
    } else {
      removeMetaTag("robots");
    }

    // Geographic tags
    setMetaTag("geo.region", geoRegion);
    setMetaTag("geo.placename", geoPlacename);
    setMetaTag("geo.position", geoPosition);
    setMetaTag("ICBM", geoPosition.replace(";", ", "));

    // Keywords
    if (keywords) {
      setMetaTag("keywords", keywords);
    }

    return () => {
      if (robots) {
        removeMetaTag("robots");
      }
    };
  }, [title, description, ogImage, ogType, canonicalPath, twitterCardType, robots, geoRegion, geoPlacename, geoPosition, keywords]);
}
